import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  Form, Button, Row, Col, Alert, ListGroup, Spinner, Badge,
} from 'react-bootstrap';
import _ from 'lodash';
import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN } from '../../config/config';
import { fetchAddressSuggestions, fetchRegionSuggestions } from '../../Services/GeoService';
import {
  createGeoJSONCircle,
  createGeoJSONPolygon,
  calculateCentroid,
  extractPolygonCoordinates,
  DEFAULT_CENTER,
} from './geoUtils';

import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

/*
 * Controlled Mapbox editor for a service area's geographic_definition.
 *
 * Props:
 *   value: {
 *     type: 'radius' | 'polygon',
 *     center?: { latitude, longitude },
 *     radius_km?: number,
 *     polygon_coordinates?: [{ latitude, longitude }],
 *   }
 *   onChange(nextValue): called with the new geographic_definition object.
 *   active: boolean — when false the map is not mounted (cheap to render in
 *           lists / tabs that are hidden).
 *   mapHeight: optional CSS height for the map container.
 *
 * The component owns the Mapbox instance + visualisation lifecycle but
 * delegates state ownership to the parent. Behaviour is intentionally a
 * verbatim port of ServiceAreaModal's previous inline map code.
 */
function ServiceAreaGeoEditor({
  value,
  onChange,
  active = true,
  mapHeight = '450px',
}) {
  const geoType = value?.type || 'radius';
  const polygonCoords = value?.polygon_coordinates || [];

  const [isDrawing, setIsDrawing] = useState(false);

  const mapContainer = useRef(null);
  const map = useRef(null);
  const marker = useRef(null);
  const polygonMarkers = useRef([]);
  // Keep latest props in refs so map event handlers (registered once on
  // mount) always see fresh values without forcing a tear-down.
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const isDrawingRef = useRef(isDrawing);
  valueRef.current = value;
  onChangeRef.current = onChange;
  isDrawingRef.current = isDrawing;

  // Helpers that emit a new value through the parent's onChange.
  const patchGeoDef = useCallback((patch) => {
    onChangeRef.current({ ...(valueRef.current || {}), ...patch });
  }, []);

  const clearPolygonMarkers = useCallback(() => {
    polygonMarkers.current.forEach((m) => m.remove());
    polygonMarkers.current = [];
  }, []);

  // --- visualisation: polygon -------------------------------------------------
  const updatePolygon = useCallback((coordinates) => {
    if (!map.current) return;

    const doUpdate = () => {
      if (!map.current) return;
      try {
        if (map.current.getLayer('polygon-fill')) map.current.removeLayer('polygon-fill');
        if (map.current.getLayer('polygon-outline')) map.current.removeLayer('polygon-outline');
        if (map.current.getLayer('polygon-points')) map.current.removeLayer('polygon-points');
        if (map.current.getSource('polygon')) map.current.removeSource('polygon');
      } catch (e) {
        // ignore: removing non-existent layers
      }

      if (!coordinates || coordinates.length < 3) return;
      const polygonGeoJSON = createGeoJSONPolygon(coordinates);
      if (!polygonGeoJSON) return;

      try {
        map.current.addSource('polygon', { type: 'geojson', data: polygonGeoJSON });
        map.current.addLayer({
          id: 'polygon-fill',
          type: 'fill',
          source: 'polygon',
          paint: { 'fill-color': '#28a745', 'fill-opacity': 0.2 },
        });
        map.current.addLayer({
          id: 'polygon-outline',
          type: 'line',
          source: 'polygon',
          paint: { 'line-color': '#28a745', 'line-width': 2 },
        });
      } catch (e) {
        setTimeout(doUpdate, 100);
      }
    };

    if (map.current.isStyleLoaded()) doUpdate();
    else map.current.once('idle', doUpdate);
  }, []);

  // --- vertex drag / delete handlers (re-derive centroid on each mutation) ---
  const handleVertexDrag = useCallback((index, newCoord) => {
    const current = valueRef.current || {};
    const coords = [...(current.polygon_coordinates || [])];
    coords[index] = newCoord;
    onChangeRef.current({
      ...current,
      polygon_coordinates: coords,
      center: calculateCentroid(coords),
    });
  }, []);

  const handleDeletePoint = useCallback((index) => {
    const current = valueRef.current || {};
    const coords = current.polygon_coordinates || [];
    if (coords.length === 0) return;
    const next = coords.filter((_c, i) => i !== index);
    onChangeRef.current({
      ...current,
      polygon_coordinates: next,
      center: next.length > 0 ? calculateCentroid(next) : current.center,
    });
  }, []);

  // --- visualisation: draggable polygon vertices -----------------------------
  const addPolygonMarkers = useCallback((coordinates) => {
    if (!map.current || !coordinates || coordinates.length === 0) return;

    const doAddMarkers = () => {
      if (!map.current) return;
      clearPolygonMarkers();

      coordinates.forEach((coord, index) => {
        const el = document.createElement('div');
        el.className = 'polygon-vertex-marker';
        el.style.width = '14px';
        el.style.height = '14px';
        el.style.backgroundColor = '#28a745';
        el.style.border = '2px solid white';
        el.style.borderRadius = '50%';
        el.style.cursor = 'move';
        el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';

        el.addEventListener('dblclick', (e) => {
          e.stopPropagation();
          handleDeletePoint(index);
        });

        const vertexMarker = new mapboxgl.Marker({ element: el, draggable: true })
          .setLngLat([coord.longitude, coord.latitude])
          .addTo(map.current);

        vertexMarker.on('dragend', () => {
          const lngLat = vertexMarker.getLngLat();
          handleVertexDrag(index, { latitude: lngLat.lat, longitude: lngLat.lng });
        });

        polygonMarkers.current.push(vertexMarker);
      });
    };

    if (map.current.isStyleLoaded()) doAddMarkers();
    else map.current.once('idle', doAddMarkers);
  }, [clearPolygonMarkers, handleDeletePoint, handleVertexDrag]);

  // --- visualisation: radius circle ------------------------------------------
  const updateCircle = useCallback((center, radiusKm) => {
    if (!map.current || !center) return;

    const doUpdate = () => {
      if (!map.current) return;
      try {
        if (map.current.getLayer('radius-circle')) map.current.removeLayer('radius-circle');
        if (map.current.getSource('radius-circle')) map.current.removeSource('radius-circle');
      } catch (e) {
        // ignore
      }

      const circleGeoJSON = createGeoJSONCircle(
        [center.longitude, center.latitude],
        radiusKm || 10,
      );
      try {
        map.current.addSource('radius-circle', { type: 'geojson', data: circleGeoJSON });
        map.current.addLayer({
          id: 'radius-circle',
          type: 'fill',
          source: 'radius-circle',
          paint: { 'fill-color': '#007bff', 'fill-opacity': 0.2 },
        });
      } catch (e) {
        setTimeout(doUpdate, 100);
      }
    };

    if (map.current.isStyleLoaded()) doUpdate();
    else map.current.once('idle', doUpdate);
  }, []);

  // --- map click for adding polygon vertices ---------------------------------
  const handleMapClick = useCallback((e) => {
    if (!isDrawingRef.current) return;
    const newCoord = { latitude: e.lngLat.lat, longitude: e.lngLat.lng };
    const current = valueRef.current || {};
    const coords = [...(current.polygon_coordinates || []), newCoord];
    onChangeRef.current({
      ...current,
      polygon_coordinates: coords,
      center: calculateCentroid(coords),
    });
  }, []);

  // --- map mount / teardown ---------------------------------------------------
  useEffect(() => {
    if (!active || map.current) return undefined;

    const initialType = valueRef.current?.type || 'radius';
    let initialCenter;
    if (initialType === 'polygon' && valueRef.current?.polygon_coordinates?.length > 0) {
      initialCenter = calculateCentroid(valueRef.current.polygon_coordinates);
    } else {
      initialCenter = valueRef.current?.center || DEFAULT_CENTER;
    }

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [initialCenter.longitude, initialCenter.latitude],
      zoom: 10,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.current.on('load', () => {
      if (initialType === 'radius') {
        const center = valueRef.current?.center || DEFAULT_CENTER;
        marker.current = new mapboxgl.Marker({ draggable: true })
          .setLngLat([center.longitude, center.latitude])
          .addTo(map.current);
        marker.current.on('dragend', () => {
          const lngLat = marker.current.getLngLat();
          patchGeoDef({ center: { latitude: lngLat.lat, longitude: lngLat.lng } });
        });
        updateCircle(center, valueRef.current?.radius_km);
      } else if (initialType === 'polygon') {
        const coords = valueRef.current?.polygon_coordinates || [];
        if (coords.length >= 3) {
          updatePolygon(coords);
          addPolygonMarkers(coords);
        }
      }
    });

    map.current.on('click', handleMapClick);

    return () => {
      clearPolygonMarkers();
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [
    active,
    addPolygonMarkers,
    clearPolygonMarkers,
    handleMapClick,
    patchGeoDef,
    updateCircle,
    updatePolygon,
  ]);

  // --- keep visualisation in sync with value changes -------------------------
  useEffect(() => {
    if (!map.current) return;

    const center = value?.center;
    const radiusKm = value?.radius_km;
    const coords = value?.polygon_coordinates || [];

    const doUpdate = () => {
      if (!map.current) return;
      if (geoType === 'radius' && center) {
        updateCircle(center, radiusKm);
      } else if (geoType === 'polygon') {
        updatePolygon(coords);
        addPolygonMarkers(coords);
      }
    };

    if (map.current.isStyleLoaded()) doUpdate();
    else map.current.once('idle', doUpdate);
  }, [
    geoType,
    value?.center?.latitude,
    value?.center?.longitude,
    value?.radius_km,
    value?.polygon_coordinates,
    updateCircle,
    updatePolygon,
    addPolygonMarkers,
  ]);

  // --- handlers for the form controls ----------------------------------------
  const handleTypeChange = (newType) => {
    if (map.current && map.current.loaded()) {
      try {
        if (map.current.getLayer('radius-circle')) map.current.removeLayer('radius-circle');
        if (map.current.getSource('radius-circle')) map.current.removeSource('radius-circle');
        if (map.current.getLayer('polygon-fill')) map.current.removeLayer('polygon-fill');
        if (map.current.getLayer('polygon-outline')) map.current.removeLayer('polygon-outline');
        if (map.current.getSource('polygon')) map.current.removeSource('polygon');
      } catch (e) {
        // ignore
      }
      clearPolygonMarkers();
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
    }

    const nextValue = { ...(value || {}), type: newType };
    onChange(nextValue);

    if (map.current && map.current.loaded() && newType === 'radius') {
      const center = nextValue.center || DEFAULT_CENTER;
      marker.current = new mapboxgl.Marker({ draggable: true })
        .setLngLat([center.longitude, center.latitude])
        .addTo(map.current);
      marker.current.on('dragend', () => {
        const lngLat = marker.current.getLngLat();
        patchGeoDef({ center: { latitude: lngLat.lat, longitude: lngLat.lng } });
      });
      updateCircle(center, nextValue.radius_km);
    }

    setIsDrawing(false);
  };

  const handleStartDrawing = () => {
    setIsDrawing(true);
    patchGeoDef({ polygon_coordinates: [] });
    clearPolygonMarkers();
    if (map.current && map.current.loaded()) {
      try {
        if (map.current.getLayer('polygon-fill')) map.current.removeLayer('polygon-fill');
        if (map.current.getLayer('polygon-outline')) map.current.removeLayer('polygon-outline');
        if (map.current.getSource('polygon')) map.current.removeSource('polygon');
      } catch (e) {
        // ignore
      }
    }
  };

  const handleFinishDrawing = () => {
    setIsDrawing(false);
    if (polygonCoords.length >= 3) {
      updatePolygon(polygonCoords);
      addPolygonMarkers(polygonCoords);
    }
  };

  const handleClearPolygon = () => {
    patchGeoDef({ polygon_coordinates: [] });
    clearPolygonMarkers();
    if (map.current && map.current.loaded()) {
      try {
        if (map.current.getLayer('polygon-fill')) map.current.removeLayer('polygon-fill');
        if (map.current.getLayer('polygon-outline')) map.current.removeLayer('polygon-outline');
        if (map.current.getSource('polygon')) map.current.removeSource('polygon');
      } catch (e) {
        // ignore
      }
    }
    setIsDrawing(false);
  };

  const handleRemoveLastPoint = () => {
    const coords = polygonCoords;
    if (coords.length === 0) return;
    const next = coords.slice(0, -1);
    onChange({
      ...value,
      polygon_coordinates: next,
      center: next.length > 0 ? calculateCentroid(next) : value.center,
    });
  };

  const handleCenterChange = (field, raw) => {
    const newCenter = { ...(value?.center || {}), [field]: Number(raw) };
    onChange({ ...value, center: newCenter });
    if (marker.current) {
      marker.current.setLngLat([newCenter.longitude, newCenter.latitude]);
      map.current?.flyTo({ center: [newCenter.longitude, newCenter.latitude] });
    }
  };

  // --- search ----------------------------------------------------------------
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);

  const debouncedSearch = useCallback(
    _.debounce(async (query, searchGeoType) => {
      if (!query || query.length < 2) {
        setSearchResults([]);
        setShowSearchResults(false);
        return;
      }
      setSearchLoading(true);
      try {
        const results = searchGeoType === 'polygon'
          ? await fetchRegionSuggestions(query)
          : await fetchAddressSuggestions(query);
        setSearchResults(results || []);
        setShowSearchResults(true);
      } catch (err) {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300),
    [],
  );

  const handleSearchChange = (e) => {
    const { value: q } = e.target;
    setSearchQuery(q);
    debouncedSearch(q, geoType);
  };

  const handleSearchSelect = (result) => {
    const newCenter = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
    };

    if (geoType === 'polygon' && result.geojson) {
      const polygonCoordinates = extractPolygonCoordinates(result.geojson);
      if (polygonCoordinates.length >= 3) {
        const centroid = calculateCentroid(polygonCoordinates);
        onChange({
          ...value,
          center: centroid,
          polygon_coordinates: polygonCoordinates,
        });

        if (map.current && result.boundingbox) {
          const bounds = new mapboxgl.LngLatBounds(
            [parseFloat(result.boundingbox[2]), parseFloat(result.boundingbox[0])],
            [parseFloat(result.boundingbox[3]), parseFloat(result.boundingbox[1])],
          );
          map.current.fitBounds(bounds, { padding: 20 });
          updatePolygon(polygonCoordinates);
          addPolygonMarkers(polygonCoordinates);
        }
        setIsDrawing(false);
      }
    } else {
      onChange({ ...value, center: newCenter });
      if (marker.current && geoType === 'radius') {
        marker.current.setLngLat([newCenter.longitude, newCenter.latitude]);
      }
      if (map.current) {
        map.current.flyTo({
          center: [newCenter.longitude, newCenter.latitude],
          zoom: 12,
        });
      }
    }

    setSearchQuery(result.displayName || '');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // --- render ----------------------------------------------------------------
  return (
    <Row>
      <Col md={6}>
        <Form.Group className="mb-3">
          <Form.Label>Type</Form.Label>
          <Form.Select
            value={geoType}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <option value="radius">Radius (Circle)</option>
            <option value="polygon">Polygon (Custom Shape)</option>
          </Form.Select>
        </Form.Group>

        {geoType === 'radius' && (
          <>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Center Latitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    value={value?.center?.latitude ?? ''}
                    onChange={(e) => handleCenterChange('latitude', e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Center Longitude</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.0001"
                    value={value?.center?.longitude ?? ''}
                    onChange={(e) => handleCenterChange('longitude', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className="mb-3">
              <Form.Label>Radius (km)</Form.Label>
              <Form.Control
                type="number"
                step="1"
                value={value?.radius_km ?? 10}
                onChange={(e) => patchGeoDef({ radius_km: Number(e.target.value) })}
                min={1}
              />
            </Form.Group>
          </>
        )}

        {geoType === 'polygon' && (
          <>
            <div className="d-flex gap-2 mb-3">
              {!isDrawing ? (
                <Button variant="success" size="sm" onClick={handleStartDrawing}>
                  <i className="fa fa-pencil-alt me-2" />
                  Draw Polygon
                </Button>
              ) : (
                <>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleFinishDrawing}
                    disabled={polygonCoords.length < 3}
                  >
                    <i className="fa fa-check me-2" />
                    Finish Drawing
                  </Button>
                  <Button
                    variant="warning"
                    size="sm"
                    onClick={handleRemoveLastPoint}
                    disabled={polygonCoords.length === 0}
                  >
                    <i className="fa fa-undo me-2" />
                    Undo
                  </Button>
                </>
              )}
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearPolygon}
                disabled={polygonCoords.length === 0}
              >
                <i className="fa fa-trash me-2" />
                Clear
              </Button>
            </div>

            {isDrawing && (
              <Alert variant="info" className="py-2">
                <small>
                  <i className="fa fa-info-circle me-2" />
                  Click on the map to add points. Add at least 3 points to create a polygon.
                </small>
              </Alert>
            )}

            <div className="mb-3">
              <small className="text-muted">
                Points:
                {' '}
                {polygonCoords.length}
                {polygonCoords.length > 0 && polygonCoords.length < 3 && (
                  <span className="text-warning ms-2">
                    (need at least 3)
                  </span>
                )}
                {polygonCoords.length >= 3 && (
                  <span className="text-success ms-2">
                    <i className="fa fa-check" />
                  </span>
                )}
              </small>
            </div>
          </>
        )}
      </Col>

      <Col md={6}>
        <div className="mb-3 position-relative" ref={searchRef}>
          <Form.Group>
            <Form.Label>
              <i className="fa fa-search me-2" />
              Search Location
            </Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder={geoType === 'polygon'
                  ? 'Search region (e.g., Hungary, Budapest)...'
                  : 'Search for an address or place...'}
                onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              />
              {searchLoading && (
                <div
                  className="position-absolute"
                  style={{ right: '10px', top: '50%', transform: 'translateY(-50%)' }}
                >
                  <Spinner size="sm" animation="border" variant="secondary" />
                </div>
              )}
            </div>
          </Form.Group>

          {showSearchResults && searchResults.length > 0 && (
            <ListGroup
              className="position-absolute w-100 shadow"
              style={{ zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}
            >
              {searchResults.map((result) => (
                <ListGroup.Item
                  key={`${result.lat}-${result.lon}`}
                  action
                  onClick={() => handleSearchSelect(result)}
                  className="py-2"
                >
                  <div className="d-flex justify-content-between align-items-start">
                    <small className="flex-grow-1">{result.displayName}</small>
                    {geoType === 'polygon' && result.geojson && (
                      <Badge bg="success" className="ms-2 flex-shrink-0">
                        <i className="fa fa-draw-polygon me-1" />
                        Boundary
                      </Badge>
                    )}
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </div>

        <div
          ref={mapContainer}
          style={{
            height: mapHeight,
            borderRadius: '8px',
            cursor: isDrawing ? 'crosshair' : 'grab',
          }}
        />
        <Form.Text className="text-muted">
          {geoType === 'radius' && (
            <>
              <i className="fa fa-arrows-alt me-1" />
              Drag the marker to set the center point
            </>
          )}
          {geoType === 'polygon' && isDrawing && (
            <>
              <i className="fa fa-mouse-pointer me-1" />
              Click on the map to add polygon points
            </>
          )}
          {geoType === 'polygon' && !isDrawing && (
            <>
              <i className="fa fa-arrows-alt me-1" />
              Drag markers to adjust
              <span className="mx-2">•</span>
              <i className="fa fa-mouse-pointer me-1" />
              Double-click to delete
            </>
          )}
        </Form.Text>
      </Col>
    </Row>
  );
}

export default ServiceAreaGeoEditor;
